import {
  PaymentChannelLiteModel,
  PaymentChannelModel,
  getPaymentChannelKey,
  getPaymentChannelsKey,
  useGetPaymentChannel,
  useGetPaymentChannels,
} from 'api-hooks/payment-channel';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: PaymentChannelLiteModel | PaymentChannelModel,
): Option {
  return {
    id: item.id,
    label: item.name,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value: OnChangeParams) => void;
  onAfterDetailChange?: (value: PaymentChannelModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function PaymentChannelSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:payment_channel'),
    placeholder = t('common:select_payment_channel'),
    noMargin = false,
  } = props;

  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('payment-channels');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetPaymentChannels,
    useDetailLazyQueryHook: useGetPaymentChannel,
    listKeyFunction: getPaymentChannelsKey,
    detailKeyFunction: getPaymentChannelKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: { is_active: true },
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.PaymentChannelsCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, PaymentChannelModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={noMargin}
      disabled={props.disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
